import findIndex from 'lodash/findIndex';
import React, { useCallback, useMemo } from 'react';

import { CollectionTargetLocation, ContentCollection } from 'craft/types';

import { FallbacksEditorToolbar } from './FallbacksEditorToolbar';
import { FallbacksEditorToolbox } from './FallbacksEditorToolbox';
import { FallbacksEditorProps, FieldData, VisibilityOption } from './types';

export const FallbacksEditor = ({
  context,
  collections,
  setCustom,
  nodeIds,
  sortOrder,
}: FallbacksEditorProps) => {
  const collectionId = useMemo(
    () => (collections && collections.length ? collections[0].collectionId : undefined),
    [collections],
  );

  const sortFields = useCallback(
    (a: FieldData, b: FieldData) =>
      sortOrder.indexOf(a.targetLocation) - sortOrder.indexOf(b.targetLocation),
    [sortOrder],
  );

  const fieldData: FieldData[] = useMemo(
    () =>
      collections
        ?.map((field: ContentCollection) => ({
          targetLocation: field.targetLocation,
          fallback: field.fallback,
        }))
        .sort(sortFields),
    [collections, sortOrder],
  );

  const hasFallback = useMemo(
    () => (fieldData ? fieldData.some((field: FieldData) => !!field.fallback) : false),
    [collections],
  );

  const fallbackMappings = useMemo(
    () =>
      fieldData?.reduce((acc, field) => {
        return {
          ...acc,
          [field.targetLocation]:
            field.targetLocation === CollectionTargetLocation.Text
              ? field.fallback
              : field.fallback?.replace(/https?:\/\//, ''),
        };
      }, {}),
    [collections],
  );

  const resetFallbacks = useCallback(() => {
    const updatedCollections = collections.map((collection: ContentCollection) => ({
      ...collection,
      fallback: undefined,
    }));
    setCustom({ collections: updatedCollections });
  }, [collections, setCustom]);

  const onSetFallback = useCallback(
    (fallbackValue?: string, targetLocation?: CollectionTargetLocation) => {
      const index = findIndex(collections, {
        targetLocation,
      });
      if (index > -1 && fallbackValue !== collections[index].fallback) {
        const updatedCollections = [...collections];
        updatedCollections[index] = { ...updatedCollections[index], fallback: fallbackValue };
        setCustom({ collections: updatedCollections });
      }
    },
    [collections, setCustom],
  );

  const onChangeVisibility = useCallback(
    (value: VisibilityOption) => {
      if (value === 'hide') {
        resetFallbacks();
      }
    },
    [resetFallbacks],
  );

  const onSetImageFromLibrary = useCallback(
    (targetLocation: CollectionTargetLocation, url: string) => {
      onSetFallback(url, targetLocation);
    },
    [onSetFallback],
  );

  const onBlur = useCallback(
    (value: string, targetLocation: CollectionTargetLocation) => {
      onSetFallback(
        targetLocation === CollectionTargetLocation.Text ? value : `https://${value}`,
        targetLocation,
      );
    },
    [onSetFallback],
  );

  // Don't show fallback options if no fields are selected
  if (!fieldData || fieldData.length === 0) {
    return <></>;
  }

  if (context?.isPopover) {
    return (
      <FallbacksEditorToolbar
        collectionId={collectionId}
        hasFallback={hasFallback}
        fieldData={fieldData}
        fallbackMappings={fallbackMappings}
        onChangeVisibility={onChangeVisibility}
        onSetImageFromLibrary={onSetImageFromLibrary}
        onBlur={onBlur}
        nodeIds={nodeIds}
      />
    );
  }
  return (
    <FallbacksEditorToolbox
      collectionId={collectionId}
      hasFallback={hasFallback}
      fieldData={fieldData}
      fallbackMappings={fallbackMappings}
      onChangeVisibility={onChangeVisibility}
      onSetImageFromLibrary={onSetImageFromLibrary}
      onBlur={onBlur}
      nodeIds={nodeIds}
    />
  );
};
